import React, {useEffect, useState} from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    Paper,
    IconButton,
    Tooltip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    Description as AgreementIcon,
    Add as AddIcon,
    Visibility as ViewIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    FileCopy as CopyIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
    Cancel as CancelIcon,
    Settings as SettingsIcon,
    Close as CloseIcon,
    Edit as SignIcon,
    Info as InfoIcon
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import {ApiDetails} from "../../../../Constants";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import SignaturePad from "./components/SignaturePad.jsx";
import SignatureSelector from "./components/SignatureSelector.jsx";
import SignatureStatusModal from "./components/SignatureStatusModal.jsx";
import TemplateManagementComponent from "../../../TemplateManagement/TemplateManagement.jsx";
import {
    landlordSignAgreement,
    getSignatureStatus,
    canSignAgreement,
    getSignatureStatusText
} from "./services/agreementSignatureApi.js";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';


const TenantAgreements = ({tenantDetails, landlordID, propertyID, token, isVacated}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State management
    const [agreements, setAgreements] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generatingAgreement, setGeneratingAgreement] = useState(false);

    // Modal states
    const [generateModal, setGenerateModal] = useState(false);
    const [viewAgreementModal, setViewAgreementModal] = useState(false);
    const [templateManagementModal, setTemplateManagementModal] = useState(false);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
    const [signatureModal, setSignatureModal] = useState(false);
    const [signatureStatusModal, setSignatureStatusModal] = useState(false);

    // Selected data
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [selectedAgreement, setSelectedAgreement] = useState(null);
    const [agreementToDelete, setAgreementToDelete] = useState(null);

    // Filtering and pagination
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Template management
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [deletingAgreement, setDeletingAgreement] = useState(false);

    // Signature management
    const [signingAgreement, setSigningAgreement] = useState(false);
    const [loadingSignatureStatus, setLoadingSignatureStatus] = useState(false);
    const [signatureStatus, setSignatureStatus] = useState(null);
    const [agreementToSign, setAgreementToSign] = useState(null);

    // Agreement content loading
    const [loadingAgreementContent, setLoadingAgreementContent] = useState(false);

    // Validation helper
    const validateRequiredData = () => {
        if (!tenantDetails?.tenant_id) {
            showSnackbar({
                title: "Error!",
                message: "Tenant ID is missing. Cannot proceed.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return false;
        }

        if (!propertyID) {
            showSnackbar({
                title: "Error!",
                message: "Property ID is missing. Cannot proceed.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return false;
        }

        if (!landlordID) {
            showSnackbar({
                title: "Error!",
                message: "Landlord ID is missing. Cannot proceed.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return false;
        }

        if (!token) {
            showSnackbar({
                title: "Error!",
                message: "Authentication token is missing. Please log in again.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return false;
        }

        return true;
    };

    // Fetch agreements on mount
    useEffect(() => {
        if (validateRequiredData()) {
            fetchAgreements();
        }
    }, [tenantDetails?.tenant_id, propertyID]);

    // Fetch all agreements for this tenant
    const fetchAgreements = () => {
        if (!validateRequiredData()) return;

        setLoading(true);

        const requestBody = {
            tenant_id: tenantDetails.tenant_id,
            property_id: propertyID,
            landlord_id: landlordID
        };

        console.log("Fetching agreements with:", requestBody);

        fetch(ApiDetails + "pegasus/visionary/agreements/fetch", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify(requestBody),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }

                const data = await response.json();

                if (response.status === 200 || response.status === 201) {
                    // Handle both array and object response
                    let agreementsData = Array.isArray(data) ? data : (data.agreements || []);

                    // Map backend structure to expected frontend structure
                    agreementsData = agreementsData.map(agreement => ({
                        id: agreement.id,  // Backend uses 'id' not 'agreement_id'
                        agreement_id: agreement.id,  // Keep both for compatibility
                        tenant_id: agreement.tenant_id,
                        property_id: agreement.property_id,
                        template_id: agreement.template_id,
                        template_name: agreement.agreement_title || agreement.template_name || "Default Template",
                        tenant_name: agreement.tenant_name,
                        property_name: agreement.property_name,
                        location: agreement.location,
                        status: agreement.status || "DRAFT",
                        version: agreement.version,
                        created_at: agreement.generated_at || agreement.created_at,
                        updated_at: agreement.generated_at || agreement.updated_at,
                        landlord_signed: agreement.landlord_signed,
                        tenant_signed: agreement.tenant_signed,
                        fully_signed: agreement.fully_signed,
                        has_pdf: agreement.has_pdf
                    }));

                    setAgreements(agreementsData);

                    if (agreementsData.length === 0) {
                        console.log("No agreements found for tenant");
                    }
                } else {
                    console.error("Failed to fetch agreements:", data);
                    showSnackbar({
                        title: "Error!",
                        message: data.message || "Failed to load agreements.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching agreements:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed to connect to server. Please try again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            });
    };

    // Fetch available templates
    const fetchTemplates = () => {
        if (!validateRequiredData()) return;

        setLoadingTemplates(true);

        const requestBody = {
            landlord_id: landlordID,
            property_id: propertyID
        };

        console.log("Fetching templates with:", requestBody);

        fetch(ApiDetails + "pegasus/visionary/agreements/templates/fetch", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify(requestBody),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }

                const data = await response.json();

                if (response.status === 200) {
                    // Handle both array and object response
                    const templatesData = Array.isArray(data) ? data : (data.templates || []);
                    setTemplates(templatesData);

                    // Auto-select default template if available
                    const defaultTemplate = templatesData.find(t => t.is_default === true || t.is_default === 1);
                    if (defaultTemplate && !selectedTemplate) {
                        // Backend returns 'id' not 'template_id'
                        setSelectedTemplate(defaultTemplate.id || defaultTemplate.template_id);
                    }
                } else {
                    console.error("Failed to fetch templates:", data);
                    showSnackbar({
                        title: "Warning!",
                        message: "Failed to load templates. Using system default.",
                        type: "warning",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setLoadingTemplates(false);
            })
            .catch((err) => {
                console.error("Error fetching templates:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed to load templates.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoadingTemplates(false);
            });
    };

    // Generate new agreement
    const generateAgreement = () => {
        if (!validateRequiredData()) return;

        if (!selectedTemplate) {
            showSnackbar({
                title: "Error!",
                message: "Please select a template.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setGeneratingAgreement(true);

        const requestBody = {
            tenant_id: tenantDetails.tenant_id,
            property_id: propertyID,
            landlord_id: landlordID,
            template_id: selectedTemplate
        };

        console.log("Generating agreement with:", requestBody);

        fetch(ApiDetails + "pegasus/visionary/agreements/generate", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify(requestBody),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }

                const data = await response.json();

                // Handle both 200 and 201 status codes
                if (response.status === 200 || response.status === 201) {
                    showSnackbar({
                        title: "Success!",
                        message: data.message || "Agreement generated successfully!",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });

                    // Backend returns different structure - map to expected format
                    const agreementData = {
                        id: data.agreement_id,  // Backend uses numeric ID
                        agreement_id: data.agreement_id,  // Keep both for compatibility
                        tenant_id: tenantDetails.tenant_id,
                        property_id: propertyID,
                        template_id: selectedTemplate,
                        template_name: data.template_used || data.agreement_title || "Default Template",
                        content: data.generated_content || data.content,
                        status: data.status || "DRAFT",
                        version: data.version || 1,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        tenant_name: `${tenantDetails.first_name} ${tenantDetails.last_name}`,
                        property_name: tenantDetails.property_name,
                        location: tenantDetails.location || "",
                        landlord_signed: false,
                        tenant_signed: false,
                        fully_signed: false,
                        has_pdf: data.has_pdf || false
                    };

                    // Set the generated agreement for immediate viewing
                    setSelectedAgreement(agreementData);
                    setGenerateModal(false);
                    setViewAgreementModal(true);

                    // Refresh agreements list
                    fetchAgreements();
                } else {
                    console.error("Failed to generate agreement:", data);
                    showSnackbar({
                        title: "Error!",
                        message: data.message || "Failed to generate agreement.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setGeneratingAgreement(false);
            })
            .catch((err) => {
                console.error("Error generating agreement:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed to generate agreement. Please try again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setGeneratingAgreement(false);
            });
    };

    // Download agreement as PDF (requires landlord signature)
    const downloadAgreement = (agreement) => {
        if (!agreement || !agreement.id) {
            showSnackbar({
                title: "Error!",
                message: "Invalid agreement data.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Validate that landlord has signed (tenant signature optional for offline signing)
        if (!agreement.landlord_signed) {
            showSnackbar({
                title: "Cannot Download",
                message: "Landlord must sign the agreement before downloading. Please sign the agreement first.",
                type: "warning",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        const requestBody = {
            agreement_id: agreement.id,
            landlord_id: landlordID,
            format: 'pdf'
        };

        console.log("Downloading agreement as PDF:", requestBody);

        fetch(ApiDetails + "pegasus/visionary/agreements/download", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify(requestBody),
        })
            .then(async (response) => {
                if (response.status === 403) {
                    localStorage.clear();
                    window.location.href = "/";
                } else if (response.status === 200) {
                    // Handle PDF response
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Agreement_${tenantDetails.first_name}_${tenantDetails.last_name}_${agreement.id}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    showSnackbar({
                        title: "Success!",
                        message: "Agreement downloaded as PDF.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else {
                    // Handle JSON error response
                    const data = await response.json();
                    showSnackbar({
                        title: "Error!",
                        message: data.message || "Failed to download agreement PDF.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.error("Error downloading agreement:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed to download agreement PDF.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    // Fetch full agreement content
    const fetchAgreementContent = async (agreementId) => {
        const requestBody = {
            agreement_id: agreementId,
            landlord_id: landlordID
        };

        console.log("Fetching agreement content with:", requestBody);

        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/agreements/view", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": token
                },
                body: JSON.stringify(requestBody),
            });

            if (response.status === 403) {
                localStorage.clear();
                window.location.href = "/";
                return null;
            }

            const data = await response.json();

            if (response.status === 200 && data.success) {
                return data.agreement;
            } else {
                showSnackbar({
                    title: "Error!",
                    message: data.message || "Failed to load agreement content.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return null;
            }
        } catch (err) {
            console.error("Error fetching agreement content:", err);
            showSnackbar({
                title: "Error!",
                message: "Failed to load agreement content.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return null;
        }
    };

    // View agreement details
    const viewAgreement = async (agreement) => {
        if (!agreement || !agreement.id) {
            showSnackbar({
                title: "Error!",
                message: "Invalid agreement data.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Open modal first with basic info
        setSelectedAgreement(agreement);
        setViewAgreementModal(true);
        setLoadingAgreementContent(true);

        // Fetch full content
        const fullAgreement = await fetchAgreementContent(agreement.id);

        if (fullAgreement) {
            // Merge full content with existing agreement data
            setSelectedAgreement({
                ...agreement,
                content: fullAgreement.content || fullAgreement.generated_content,
                // Include any other fields from the full response
                agreement_title: fullAgreement.agreement_title || agreement.template_name,
                version: fullAgreement.version || agreement.version
            });
        }

        setLoadingAgreementContent(false);
    };

    // Delete agreement (soft delete)
    const deleteAgreement = () => {
        if (!agreementToDelete || !agreementToDelete.id) {
            showSnackbar({
                title: "Error!",
                message: "Invalid agreement data.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setDeletingAgreement(true);

        const requestBody = {
            agreement_id: agreementToDelete.id,  // Backend expects agreement_id but we store as 'id'
            landlord_id: landlordID
        };

        console.log("Deleting agreement with:", requestBody);

        fetch(ApiDetails + "pegasus/visionary/agreements/delete", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify(requestBody),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }

                const data = await response.json();

                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Agreement deleted successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });

                    setDeleteConfirmModal(false);
                    setAgreementToDelete(null);
                    fetchAgreements();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: data.message || "Failed to delete agreement.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setDeletingAgreement(false);
            })
            .catch((err) => {
                console.error("Error deleting agreement:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed to delete agreement.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setDeletingAgreement(false);
            });
    };

    // Copy agreement content to clipboard
    const copyToClipboard = (content) => {
        if (!content) {
            showSnackbar({
                title: "Error!",
                message: "No content to copy.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        navigator.clipboard.writeText(content).then(() => {
            showSnackbar({
                title: "Success!",
                message: "Agreement content copied to clipboard.",
                type: "success",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }).catch((err) => {
            console.error("Failed to copy:", err);
            showSnackbar({
                title: "Error!",
                message: "Failed to copy to clipboard.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        });
    };

    // Open generate modal and fetch templates
    const handleOpenGenerateModal = () => {
        setGenerateModal(true);
        fetchTemplates();
    };

    // Landlord sign agreement
    const openSignatureModal = (agreement) => {
        // Check if landlord can sign
        const signCheck = canSignAgreement(agreement, 'landlord');

        if (!signCheck.canSign) {
            showSnackbar({
                title: "Cannot Sign",
                message: signCheck.reason,
                type: "warning",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setAgreementToSign(agreement);
        setSignatureModal(true);
    };

    // Handle signature selection from SignatureSelector component
    const handleSignatureSelected = async (result) => {
        console.log('handleSignatureSelected called with result:', result);

        if (!result.success || !agreementToSign) {
            showSnackbar({
                title: "Error!",
                message: "Failed to select signature. Please try again.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        try {
            setSigningAgreement(true);

            // SignatureSelector has already signed the agreement
            // result.signResult contains the backend response
            const signResult = result.signResult;

            if (signResult && (signResult.success !== false)) {
                showSnackbar({
                    title: "Success!",
                    message: `Agreement signed successfully using ${result.signatureMethod === 'new' ? 'new' : 'stored'} signature!`,
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });

                // Close signature modal
                setSignatureModal(false);
                setAgreementToSign(null);

                // Refresh agreements list
                fetchAgreements();

                // Show next action info based on response
                if (signResult.status === 'FULLY_SIGNED' || signResult.fully_signed) {
                    showSnackbar({
                        title: "Fully Signed!",
                        message: "Agreement is now fully signed by both parties. You can download it.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else {
                    showSnackbar({
                        title: "Next Step",
                        message: "Waiting for tenant signature. Consider sending them a notification.",
                        type: "info",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            } else {
                showSnackbar({
                    title: "Error!",
                    message: signResult?.message || signResult?.error || "Failed to sign agreement",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        } catch (err) {
            console.error("Error handling signature selection:", err);
            showSnackbar({
                title: "Error!",
                message: "An unexpected error occurred while processing the agreement",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        } finally {
            setSigningAgreement(false);
        }
    };

    // Handle signature save (legacy - kept for backward compatibility)
    const handleSignatureSave = async (signatureData) => {
        if (!agreementToSign) return;

        setSigningAgreement(true);

        const result = await landlordSignAgreement(
            agreementToSign.id,
            signatureData,
            token
        );

        if (result.success) {
            showSnackbar({
                title: "Success!",
                message: result.data.message || "Agreement signed successfully!",
                type: "success",
                position: {vertical: "bottom", horizontal: "right"},
            });

            // Close signature modal
            setSignatureModal(false);
            setAgreementToSign(null);

            // Refresh agreements list
            fetchAgreements();

            // Show next action info
            if (result.data.fully_signed) {
                showSnackbar({
                    title: "Fully Signed!",
                    message: "Agreement is now fully signed by both parties. You can download it.",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            } else {
                showSnackbar({
                    title: "Next Step",
                    message: "Waiting for tenant signature. Consider sending them a notification.",
                    type: "info",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        } else {
            showSnackbar({
                title: "Error!",
                message: result.error,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }

        setSigningAgreement(false);
    };

    // View signature status
    const viewSignatureStatus = async (agreement) => {
        setSelectedAgreement(agreement);
        setSignatureStatusModal(true);
        setLoadingSignatureStatus(true);

        const result = await getSignatureStatus(agreement.id, token, true);

        if (result.success) {
            setSignatureStatus(result.data);
        } else {
            showSnackbar({
                title: "Error!",
                message: result.error,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }

        setLoadingSignatureStatus(false);
    };

    // Download signed agreement
    const downloadSignedAgreement = () => {
        if (selectedAgreement) {
            downloadAgreement(selectedAgreement);
        }
    };

    // Close all modals
    const handleCloseModals = () => {
        setGenerateModal(false);
        setViewAgreementModal(false);
        setTemplateManagementModal(false);
        setDeleteConfirmModal(false);
        setSignatureModal(false);
        setSignatureStatusModal(false);
        setSelectedTemplate("");
        setSelectedAgreement(null);
        setAgreementToDelete(null);
        setAgreementToSign(null);
        setSignatureStatus(null);
    };

    // Get status chip color
    const getStatusColor = (status) => {
        const statusMap = {
            'DRAFT': 'default',
            'PENDING_SIGNATURE': 'warning',
            'SIGNED': 'success',
            'ACTIVE': 'success',
            'EXPIRED': 'error',
            'TERMINATED': 'error',
            'ARCHIVED': 'default'
        };
        return statusMap[status] || 'default';
    };

    // Get status icon
    const getStatusIcon = (status) => {
        if (status === 'SIGNED' || status === 'ACTIVE') {
            return <CheckCircleIcon fontSize="small"/>;
        } else if (status === 'PENDING_SIGNATURE') {
            return <PendingIcon fontSize="small"/>;
        } else if (status === 'EXPIRED' || status === 'TERMINATED') {
            return <CancelIcon fontSize="small"/>;
        }
        return null;
    };

    // Filter agreements by status
    const filteredAgreements = statusFilter === "ALL"
        ? agreements
        : agreements.filter(agreement => agreement.status === statusFilter);

    // Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Get paginated agreements
    const paginatedAgreements = filteredAgreements.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                    <FormControl size="small" sx={{minWidth: 150}}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="ALL">All Agreements</MenuItem>
                            <MenuItem value="DRAFT">Draft</MenuItem>
                            <MenuItem value="PENDING_SIGNATURE">Pending Signature</MenuItem>
                            <MenuItem value="SIGNED">Signed</MenuItem>
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="EXPIRED">Expired</MenuItem>
                            <MenuItem value="TERMINATED">Terminated</MenuItem>
                        </Select>
                    </FormControl>

                    <Chip
                        label={`${filteredAgreements.length} Agreement${filteredAgreements.length !== 1 ? 's' : ''}`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>

                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    <Tooltip title={isVacated ? "Cannot generate agreements for vacated tenant" : ""} arrow>
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon/>}
                                onClick={handleOpenGenerateModal}
                                disabled={isVacated || !validateRequiredData()}
                                sx={{
                                    bgcolor: '#398DD2',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    '&:hover': {
                                        bgcolor: '#2c6ba0'
                                    }
                                }}
                            >
                                Generate Agreement
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Manage Templates">
                        <IconButton
                            onClick={() => setTemplateManagementModal(true)}
                            sx={{
                                border: '1px solid #398DD2',
                                color: '#398DD2',
                                '&:hover': {
                                    bgcolor: 'rgba(57, 141, 210, 0.08)'
                                }
                            }}
                        >
                            <SettingsIcon/>
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Agreements List */}
            {loading ? (
                <Box sx={{textAlign: 'center', py: 6}}>
                    <CircularProgress/>
                    <Typography sx={{mt: 2}}>Loading agreements...</Typography>
                </Box>
            ) : filteredAgreements.length === 0 ? (
                <Box sx={{
                    textAlign: 'center',
                    py: 6,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    border: '1px dashed #ccc'
                }}>
                    <AgreementIcon sx={{fontSize: 64, color: '#ccc', mb: 2}}/>
                    <Typography variant="h6" color="textSecondary" sx={{mb: 1}}>
                        No Agreements Found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                        {statusFilter === "ALL"
                            ? "Generate your first tenancy agreement to get started."
                            : `No agreements with status: ${statusFilter}`
                        }
                    </Typography>
                    {!isVacated && statusFilter === "ALL" && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon/>}
                            onClick={handleOpenGenerateModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#398DD2',
                                color: '#398DD2'
                            }}
                        >
                            Generate First Agreement
                        </Button>
                    )}
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}}>
                        <Table>
                            <TableHead sx={{bgcolor: '#f5f5f5'}}>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 600}}>Agreement ID</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Template</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Status</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Signatures</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Created</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Updated</TableCell>
                                    <TableCell align="right" sx={{fontWeight: 600}}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedAgreements.map((agreement) => (
                                    <TableRow
                                        key={agreement.id}
                                        hover
                                        sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                                {agreement.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {agreement.template_name || 'Default Template'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={agreement.status}
                                                color={getStatusColor(agreement.status)}
                                                size="small"
                                                icon={getStatusIcon(agreement.status)}
                                                sx={{fontSize: '11px'}}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                                <Tooltip
                                                    title={agreement.landlord_signed ? "Landlord Signed" : "Landlord Not Signed"}>
                                                    <Chip
                                                        label="L"
                                                        size="small"
                                                        color={agreement.landlord_signed ? "success" : "default"}
                                                        sx={{
                                                            minWidth: '32px',
                                                            fontSize: '10px',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    title={agreement.tenant_signed ? "Tenant Signed" : "Tenant Not Signed"}>
                                                    <Chip
                                                        label="T"
                                                        size="small"
                                                        color={agreement.tenant_signed ? "success" : "default"}
                                                        sx={{
                                                            minWidth: '32px',
                                                            fontSize: '10px',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </Tooltip>
                                                {agreement.fully_signed && (
                                                    <CheckCircleIcon
                                                        fontSize="small"
                                                        sx={{color: '#4caf50', ml: 0.5}}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(agreement.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(agreement.updated_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 0.5,
                                                justifyContent: 'flex-end',
                                                flexWrap: 'wrap'
                                            }}>
                                                <Tooltip title="View Agreement">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => viewAgreement(agreement)}
                                                        sx={{color: '#398DD2'}}
                                                    >
                                                        <ViewIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Signature Status">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => viewSignatureStatus(agreement)}
                                                        sx={{color: '#9c27b0'}}
                                                    >
                                                        <InfoIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>

                                                {!agreement.landlord_signed && (
                                                    <Tooltip title="Sign Agreement">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openSignatureModal(agreement)}
                                                            sx={{color: '#ff9800'}}
                                                        >
                                                            <SignIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                <Tooltip title={
                                                    agreement.landlord_signed
                                                        ? (agreement.fully_signed ? "Download PDF (Fully Signed)" : "Download PDF (Landlord Signed)")
                                                        : "Sign agreement to download"
                                                }>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => downloadAgreement(agreement)}
                                                            disabled={!agreement.landlord_signed}
                                                            sx={{
                                                                color: agreement.landlord_signed ? '#4caf50' : 'rgba(0, 0, 0, 0.26)',
                                                                '&.Mui-disabled': {
                                                                    color: 'rgba(0, 0, 0, 0.26)'
                                                                }
                                                            }}
                                                        >
                                                            <DownloadIcon fontSize="small"/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <Tooltip
                                                    title={isVacated ? "Cannot delete for vacated tenant" : "Delete Agreement"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setAgreementToDelete(agreement);
                                                                setDeleteConfirmModal(true);
                                                            }}
                                                            disabled={isVacated}
                                                            sx={{color: '#d32f2f'}}
                                                        >
                                                            <DeleteIcon fontSize="small"/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredAgreements.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}

            {/* Generate Agreement Modal */}
            <Dialog
                open={generateModal}
                onClose={handleCloseModals}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    color: '#398DD2',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <AgreementIcon/>
                        Generate Tenancy Agreement
                    </Box>
                    <IconButton onClick={handleCloseModals} size="small">
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Divider sx={{mb: 3}}/>

                    {/* Tenant Info */}
                    <Box sx={{mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2}}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">Tenant Name</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {tenantDetails.first_name} {tenantDetails.last_name}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Unit</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {tenantDetails.house_number}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Property</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {tenantDetails.property_name}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Template Selection */}
                    <FormControl fullWidth sx={{mb: 2}}>
                        <InputLabel>Select Template</InputLabel>
                        <Select
                            value={selectedTemplate}
                            label="Select Template"
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            disabled={loadingTemplates}
                        >
                            {loadingTemplates ? (
                                <MenuItem disabled>Loading templates...</MenuItem>
                            ) : templates.length === 0 ? (
                                <MenuItem value="">System Default Template</MenuItem>
                            ) : (
                                templates.map((template) => {
                                    // Backend returns 'id' not 'template_id'
                                    const templateId = template.id || template.template_id;
                                    return (
                                        <MenuItem key={templateId} value={templateId}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%'
                                            }}>
                                                <Typography>{template.template_name}</Typography>
                                                {(template.is_default === true || template.is_default === 1) && (
                                                    <Chip label="Default" size="small" color="primary" sx={{ml: 1}}/>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    );
                                })
                            )}
                        </Select>
                    </FormControl>

                    <Typography variant="caption" color="textSecondary">
                        The agreement will be generated with real-time data from your system including tenant details,
                        property information, rent amount, and payment terms.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{px: 3, py: 2}}>
                    <Button onClick={handleCloseModals} sx={{textTransform: 'none'}}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={generateAgreement}
                        loading={generatingAgreement}
                        startIcon={<AddIcon/>}
                        variant="contained"
                        sx={{
                            bgcolor: '#398DD2',
                            textTransform: 'none',
                            '&:hover': {bgcolor: '#2c6ba0'}
                        }}
                    >
                        Generate Agreement
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* View Agreement Modal */}
            <Dialog
                open={viewAgreementModal}
                onClose={handleCloseModals}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{
                    color: '#398DD2',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <AgreementIcon/>
                        Agreement Preview
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {selectedAgreement && (
                            <Chip
                                label={selectedAgreement.status}
                                color={getStatusColor(selectedAgreement.status)}
                                size="small"
                            />
                        )}
                        <IconButton onClick={handleCloseModals} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Divider sx={{mb: 3}}/>

                    {selectedAgreement && (
                        <>
                            {/* Agreement Metadata */}
                            <Box sx={{mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2}}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Agreement ID</Typography>
                                        <Typography variant="body2" fontWeight={500} sx={{fontFamily: 'monospace'}}>
                                            {selectedAgreement.id}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Template</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {selectedAgreement.template_name || 'Default Template'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Created</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDate(selectedAgreement.created_at)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Last Updated</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDate(selectedAgreement.updated_at)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Agreement Content Preview */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    minHeight: '400px',
                                    maxHeight: '500px',
                                    overflowY: 'auto',
                                    bgcolor: 'white'
                                }}
                            >
                                {loadingAgreementContent ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '400px',
                                        gap: 2
                                    }}>
                                        <CircularProgress size={40}/>
                                        <Typography variant="body2" color="textSecondary">
                                            Loading agreement content...
                                        </Typography>
                                    </Box>
                                ) : selectedAgreement.content ? (
                                    <div dangerouslySetInnerHTML={{__html: selectedAgreement.content}}/>
                                ) : (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '400px',
                                        gap: 2
                                    }}>
                                        <Typography color="textSecondary" align="center">
                                            No content available
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" align="center">
                                            The agreement content could not be loaded. Please try again.
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{px: 3, py: 2}}>
                    <Button
                        startIcon={<CopyIcon/>}
                        onClick={() => copyToClipboard(selectedAgreement?.content)}
                        sx={{textTransform: 'none'}}
                    >
                        Copy Content
                    </Button>
                    <Tooltip title={
                        selectedAgreement?.landlord_signed
                            ? (selectedAgreement?.fully_signed ? "Fully signed by both parties" : "Landlord signed - can share with tenant")
                            : "Landlord must sign first"
                    }>
                        <span>
                            <Button
                                startIcon={<DownloadIcon/>}
                                onClick={() => downloadAgreement(selectedAgreement)}
                                disabled={!selectedAgreement?.landlord_signed}
                                sx={{textTransform: 'none'}}
                            >
                                Download PDF
                            </Button>
                        </span>
                    </Tooltip>
                    <Button onClick={handleCloseModals}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog
                open={deleteConfirmModal}
                onClose={handleCloseModals}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{color: '#d32f2f', fontWeight: 600}}>
                    Delete Agreement
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this agreement? This action will mark the agreement as deleted.
                    </Typography>
                    {agreementToDelete && (
                        <Box sx={{mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1}}>
                            <Typography variant="caption" color="textSecondary">Agreement ID</Typography>
                            <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                {agreementToDelete.id}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModals}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={deleteAgreement}
                        loading={deletingAgreement}
                        startIcon={<DeleteIcon/>}
                        variant="contained"
                        color="error"
                    >
                        Delete
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Template Management Modal */}
            <Dialog
                open={templateManagementModal}
                onClose={handleCloseModals}
                maxWidth="xl"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{
                    color: '#398DD2',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <SettingsIcon/>
                        Template Management
                    </Box>
                    <IconButton onClick={handleCloseModals} size="small">
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{p: 0}}>
                    <TemplateManagementComponent
                        landlordID={landlordID}
                        propertyID={propertyID}
                        token={token}
                    />
                </DialogContent>
            </Dialog>

            {/* Signature Modal */}
            <Dialog
                open={signatureModal}
                onClose={() => !signingAgreement && handleCloseModals()}
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown={signingAgreement}
            >
                <DialogTitle sx={{
                    color: '#398DD2',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <SignIcon/>
                        Sign Agreement
                    </Box>
                    {!signingAgreement && (
                        <IconButton onClick={handleCloseModals} size="small">
                            <CloseIcon/>
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Divider sx={{mb: 3}}/>

                    {/* Agreement Info */}
                    {agreementToSign && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: '#f5f5f5',
                                borderRadius: 2
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Agreement ID
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{fontFamily: 'monospace'}}>
                                        {agreementToSign.id}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Template
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {agreementToSign.template_name || 'Default Template'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Tenant
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {tenantDetails.first_name} {tenantDetails.last_name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Status
                                    </Typography>
                                    <Box sx={{mt: 0.5}}>
                                        <Chip
                                            label={agreementToSign.status}
                                            color={getStatusColor(agreementToSign.status)}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* Signature Selector - View all stored signatures or draw new */}
                    <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                        <SignatureSelector
                            agreementId={agreementToSign?.id}
                            token={token}
                            agreementTitle={agreementToSign?.agreement_title || agreementToSign?.template_name}
                            onSignatureSelected={handleSignatureSelected}
                            onCancel={handleCloseModals}
                            selectedAgreementIds={[agreementToSign?.id]}
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Signature Status Modal */}
            <SignatureStatusModal
                open={signatureStatusModal}
                onClose={handleCloseModals}
                agreement={selectedAgreement}
                signatureStatus={signatureStatus}
                loading={loadingSignatureStatus}
                onDownload={downloadSignedAgreement}
            />
        </Box>
    );
};

export default TenantAgreements;
